import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, errMsg, LOGO } from "../lib/api";
import { btnPrimary, inputCls } from "../components/ui-kit";

const AuthShell = ({ title, sub, children }) => (
  <div className="grid min-h-screen bg-[#0B0D12] text-slate-100 lg:grid-cols-2">
    <div className="relative hidden overflow-hidden border-r border-[#282E3D] lg:block">
      <div className="absolute inset-0 grid-bg" />
      <div className="relative flex h-full flex-col justify-between p-14">
        <img src={LOGO} alt="Mathsmania" className="h-20 w-20 rounded-xl bg-white object-contain p-1" />
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF6B00]">Operations Manager</div>
          <h2 className="mt-4 font-heading text-5xl font-extrabold leading-[1.05]">
            Every quiz, mock & note.<br /><span className="text-[#FF6B00]">One pipeline.</span>
          </h2>
          <p className="mt-6 max-w-md text-slate-400">Design processes, route work to the right role, and keep a full history of every handoff.</p>
        </div>
        <div className="font-mono text-[11px] text-slate-600">WRITE → PROOF → APPROVE → TYPE → UPLOAD</div>
      </div>
    </div>
    <div className="flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-sm animate-rise">
        <img src={LOGO} alt="Mathsmania" className="mb-8 h-14 w-14 rounded-lg bg-white object-contain p-0.5 lg:hidden" />
        <h1 className="font-heading text-4xl font-extrabold tracking-tight">{title}</h1>
        <p className="mt-2 text-sm text-slate-400">{sub}</p>
        <div className="mt-8">{children}</div>
      </div>
    </div>
  </div>
);

const Field = ({ label, ...p }) => (
  <label className="block">
    <span className="mb-1.5 block text-xs font-medium text-slate-400">{label}</span>
    <input className={inputCls} required {...p} />
  </label>
);

export function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  if (user) return <Navigate to="/" />;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await login(f.email, f.password);
      nav("/");
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthShell title="Sign in" sub="Welcome back to the Mathsmania workroom.">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Email" type="email" data-testid="login-email-input" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        <Field label="Password" type="password" data-testid="login-password-input" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
        <div className="-mt-2 text-right"><Link data-testid="forgot-password-link" to="/forgot-password" className="text-xs font-medium text-slate-400 hover:text-[#FF6B00]">Forgot password?</Link></div>
        {err && <div data-testid="login-error" className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-300">{err}</div>}
        <button data-testid="login-submit-button" disabled={busy} className={`${btnPrimary} w-full`}>{busy ? "Signing in…" : "Sign in"} <ArrowRight className="h-4 w-4" /></button>
      </form>
      <p className="mt-6 text-sm text-slate-400">New to the team? <Link data-testid="register-link" to="/register" className="font-semibold text-[#FF6B00] hover:underline">Create an account</Link></p>
    </AuthShell>
  );
}

export function ForgotPassword() {
  const { user } = useAuth();
  const [f, setF] = useState({ email: "", new_password: "", confirm: "" });
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  if (user) return <Navigate to="/" />;
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (f.new_password.length < 6) return setErr("Password must be at least 6 characters");
    if (f.new_password !== f.confirm) return setErr("Passwords don't match");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/password-reset-request", { email: f.email, new_password: f.new_password });
      setMsg(data.message);
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };
  if (msg)
    return (
      <AuthShell title="Request sent" sub="A manager needs to approve your new password.">
        <div data-testid="password-request-sent-card" className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-5 text-sm text-amber-200">{msg}</div>
        <p className="mt-4 text-xs text-slate-500">Let your manager know you sent this request. Once it's approved, sign in with your new password.</p>
        <Link data-testid="back-to-login-link" to="/login" className={`${btnPrimary} mt-6 w-full`}>Back to sign in</Link>
      </AuthShell>
    );
  return (
    <AuthShell title="Reset password" sub="Choose a new password. It starts working once a manager or the super admin approves it.">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Your email" type="email" data-testid="forgot-email-input" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        <Field label="New password (min 6 chars)" type="password" autoComplete="new-password" data-testid="forgot-new-password-input" value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} />
        <Field label="Confirm new password" type="password" autoComplete="new-password" data-testid="forgot-confirm-password-input" value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} />
        {err && <div data-testid="forgot-error" className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-300">{err}</div>}
        <button data-testid="forgot-submit-button" disabled={busy} className={`${btnPrimary} w-full`}>{busy ? "Sending…" : "Send request for approval"}</button>
      </form>
      <p className="mt-6 text-sm text-slate-400">Remembered it? <Link data-testid="login-link" to="/login" className="font-semibold text-[#FF6B00] hover:underline">Sign in</Link></p>
    </AuthShell>
  );
}

export function Register() {
  const { user } = useAuth();
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  if (user) return <Navigate to="/" />;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await api.post("/auth/register", f);
      setDone(true);
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };
  if (done)
    return (
      <AuthShell title="Awaiting approval" sub="Your account has been created.">
        <div data-testid="pending-approval-card" className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-5 text-sm text-amber-200">
          An admin needs to approve <b>{f.email}</b> and assign your role(s). You can sign in once you're approved.
        </div>
        <Link data-testid="back-to-login-link" to="/login" className={`${btnPrimary} mt-6 w-full`}>Back to sign in</Link>
      </AuthShell>
    );
  return (
    <AuthShell title="Join the team" sub="Register with your email. An admin approves every account.">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Full name" data-testid="register-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <Field label="Email" type="email" data-testid="register-email-input" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        <Field label="Password (min 6 chars)" type="password" minLength={6} data-testid="register-password-input" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
        {err && <div data-testid="register-error" className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-300">{err}</div>}
        <button data-testid="register-submit-button" disabled={busy} className={`${btnPrimary} w-full`}>{busy ? "Submitting…" : "Register"}</button>
      </form>
      <p className="mt-6 text-sm text-slate-400">Already approved? <Link data-testid="login-link" to="/login" className="font-semibold text-[#FF6B00] hover:underline">Sign in</Link></p>
    </AuthShell>
  );
}
